import React, { useState, useEffect } from 'react';
import { authFetch } from '../utils/auth';

const AuthenticatedImage = ({
  src,
  alt = 'Image',
  className = '',
  onClick = null,
  style = {},
  onLoad = null,
  onError = null
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const toEndpoint = (url) => {
      if (!url) return url;
      try {
        const parsed = new URL(url, window.location.origin);
        return parsed.pathname.replace(/['"]+$/, '') + parsed.search;
      } catch {
        return url;
      }
    };

    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(null);

        // notifyOnExpiry:false — a failed image load must never log the user out
        // of the app. A 401/403 surfaces as an inline error instead.
        const response = await authFetch(toEndpoint(src), {}, { notifyOnExpiry: false });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new Error(`Not authorized to load this image (${response.status}).`);
          } else if (response.status === 404) {
            throw new Error('Image not found');
          } else {
            throw new Error(`Failed to load image: ${response.status}`);
          }
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setImageSrc(objectUrl);
          setLoading(false);
          setError(null);
          if (onLoad) onLoad();
        }

        return () => { URL.revokeObjectURL(objectUrl); };
      } catch (err) {
        console.error('Error loading authenticated image:', err);
        if (isMounted) {
          setError(err.message);
          setLoading(false);
          if (onError) onError(err);
        }
      }
    };

    fetchImage();

    return () => { isMounted = false; };
  }, [src, onLoad, onError]);

  if (loading) {
    return (
      <div className={`inline-flex items-center justify-center bg-gray-100 ${className}`} style={style}>
        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm ${className}`}>
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={{ ...style, cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    />
  );
};

export default AuthenticatedImage;
